<script setup>
import { convertToInr } from "@/services/common-services";
import {
  donutChartOptions,
  chartColors,
  gridHighlightPlugin,
  lineChartOptions,
} from "@/services/constant-services";
import Chart from "primevue/chart";
import { onMounted, ref, computed } from "vue";

const props = defineProps({
  stocks: Array,
  mutualFunds: Array,
  fixedIncome: Array,
  otherInvestment: Array,
  summary: Array,
});

onMounted(() => {
  formatPortfolioDiversity();
});

const donutChartData = ref({ labels: [], datasets: [], rawValues: [] });

// Computed properties for KPI cards and chart
const latestSummary = computed(() => {
  // Filter out entries with zero portfolio value (incomplete/placeholder data)
  const validSummaries = (props.summary || []).filter(s => s.portfolioTotalMarketVal > 0);
  if (validSummaries.length === 0) return null;

  // Sort by date descending to get the latest valid entry
  const sorted = [...validSummaries].sort((a, b) =>
    new Date(b.lastUpdated) - new Date(a.lastUpdated)
  );
  return sorted[0] || null;
});

const prevSummary = computed(() => {
  // Filter out entries with zero portfolio value (incomplete/placeholder data)
  const validSummaries = (props.summary || []).filter(s => s.portfolioTotalMarketVal > 0);
  if (validSummaries.length < 2) return null;

  // Sort by date descending to get the second latest valid entry
  const sorted = [...validSummaries].sort((a, b) =>
    new Date(b.lastUpdated) - new Date(a.lastUpdated)
  );
  return sorted[1] || null;
});

const totalPortfolioValue = computed(() => {
  return latestSummary.value?.portfolioTotalMarketVal || 0;
});

const marketLinkedLatest = computed(() => {
  const latest = latestSummary.value;
  if (!latest) return 0;
  return (latest.stockPFMarketVal || 0) + (latest.mutualFundPFMarketVal || 0);
});

const todayChange = computed(() => {
  const latest = latestSummary.value;
  const prev = prevSummary.value;
  if (!latest || !prev) return 0;

  const marketLinkedPrev = (prev.stockPFMarketVal || 0) + (prev.mutualFundPFMarketVal || 0);
  return marketLinkedLatest.value - marketLinkedPrev;
});

const todayChangePct = computed(() => {
  const latest = latestSummary.value;
  const prev = prevSummary.value;
  if (!latest || !prev) return 0;

  const marketLinkedPrev = (prev.stockPFMarketVal || 0) + (prev.mutualFundPFMarketVal || 0);
  if (marketLinkedPrev === 0) return 0;

  return (todayChange.value / marketLinkedPrev) * 100;
});

// Check if there's a significant portfolio change (>8%) that might indicate new holdings
const hasSignificantChange = computed(() => {
  const pct = Math.abs(todayChangePct.value);
  return pct > 8;
});

// Check if this is the first data point (no previous data)
const isFirstDataPoint = computed(() => {
  return !prevSummary.value && latestSummary.value;
});

const chartLabels = computed(() => {
  // Sort by date ascending (oldest first) for chronological display
  const sortedSummary = [...(props.summary || [])].sort((a, b) =>
    new Date(a.lastUpdated) - new Date(b.lastUpdated)
  );

  return sortedSummary.map((item) =>
    new Date(item.lastUpdated).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    })
  );
});

const marketLinkedChartData = computed(() => {
  // Sort by date ascending (oldest first) for chronological display
  const sortedSummary = [...(props.summary || [])].sort((a, b) =>
    new Date(a.lastUpdated) - new Date(b.lastUpdated)
  );

  const data = sortedSummary.map((item) =>
    (item.stockPFMarketVal || 0) + (item.mutualFundPFMarketVal || 0)
  );

  return {
    labels: chartLabels.value,
    datasets: [
      {
        data,
        label: "Market-linked Portfolio (Stocks + MF)",
        tension: 0.3,
        fill: false,
        borderColor: '#3b82f6',
        backgroundColor: '#3b82f6',
      },
    ],
  };
});

const hasChartData = computed(() => {
  return props.summary && props.summary.length >= 2;
});

const hasMarketLinkedHoldings = computed(() => {
  // Check if any summary entry has non-zero market-linked value
  return (props.summary || []).some(item =>
    (item.stockPFMarketVal || 0) > 0 || (item.mutualFundPFMarketVal || 0) > 0
  );
});

const formatPortfolioDiversity = () => {
  const groupedValues = {};
  const today = new Date();

  // Stocks: market value = currentPrice * numberOfShares
  (props.stocks || []).forEach((s) => {
    const type = "Stocks";
    const value = Number(s.currentPrice || 0) * Number(s.numberOfShares || 0);
    groupedValues[type] = (groupedValues[type] || 0) + value;
  });

  // Mutual funds: NAV * units
  (props.mutualFunds || []).forEach((mf) => {
    const type = "Mutual Funds";
    const value = Number(mf.currentNAV || mf.nav || 0) * Number(mf.numberOfUnits || 0);
    groupedValues[type] = (groupedValues[type] || 0) + value;
  });

  // Fixed income: compound current value using dateUpdated and interestRate
  (props.fixedIncome || []).forEach((fi) => {
    const type = fi.investmentType || "Fixed Income";
    const principal = Number(fi.amountInvested || 0);
    const rate = Number(fi.interestRate || 0) / 100; // annual rate in decimal
    let years = 0;
    if (fi.dateUpdated) {
      const updated = new Date(fi.dateUpdated);
      const diffMs = today - updated;
      years = diffMs / (1000 * 60 * 60 * 24 * 365);
    }
    const currentVal = principal * Math.pow(1 + rate, years || 0);
    const value = isFinite(currentVal) ? currentVal : principal;
    groupedValues[type] = (groupedValues[type] || 0) + value;
  });

  // Other investments: amountInvested
  (props.otherInvestment || []).forEach((oi) => {
    const type = oi.investmentType || "Other";
    const value = Number(oi.amountInvested || 0);
    groupedValues[type] = (groupedValues[type] || 0) + value;
  });

  const labels = Object.keys(groupedValues);
  const totalValue = labels.reduce((sum, label) => sum + groupedValues[label], 0);
  const rawValues = labels.map((label) => groupedValues[label]);
  const percentages = labels.map((label) => {
    if (!totalValue) return 0;
    return ((groupedValues[label] / totalValue) * 100).toFixed(2);
  });

  donutChartData.value = {
    labels,
    datasets: [
      {
        data: percentages,
        backgroundColor: chartColors.slice(0, labels.length),
      },
    ],
    rawValues,
  };
};
</script>

<template>
  <div class="chart-wrapper mt-30 d-flex gap-20">
    <!-- Left section: KPI Cards + Market-linked Chart -->
    <div class="input-shadow line p-20 br-10 d-flex flex-direction-column">
      <!-- KPI Cards Row -->
      <div class="kpi-cards-row d-flex gap-15 mb-20">
        <!-- Card 1: Total Portfolio Value -->
        <div class="kpi-card kpi-card-primary flex-1">
          <div class="kpi-title">Total Portfolio Value</div>
          <div class="kpi-value" :style="{ color: 'var(--an-success)' }">
            {{ convertToInr(totalPortfolioValue) }}
          </div>
          <div class="kpi-subtitle">
            As of {{ latestSummary ? new Date(latestSummary.lastUpdated).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-' }}
          </div>
          <div class="kpi-breakdown">
            <div class="breakdown-item">
              <span class="breakdown-bullet">•</span>
              <span>Stocks: {{ convertToInr(latestSummary?.stockPFMarketVal || 0) }}</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-bullet">•</span>
              <span>Mutual Funds: {{ convertToInr(latestSummary?.mutualFundPFMarketVal || 0) }}</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-bullet">•</span>
              <span>Fixed Income: {{ convertToInr(latestSummary?.otherTMV || 0) }}</span>
            </div>
          </div>
        </div>

        <!-- Card 2: Today's Change -->
        <div class="kpi-card kpi-card-secondary flex-1">
          <!-- Special case: First data point (no history) -->
          <template v-if="isFirstDataPoint">
            <div class="kpi-title">Portfolio Tracking Started</div>
            <div class="kpi-value" :style="{ color: 'var(--an-primary, #3b82f6)' }">
              {{ convertToInr(marketLinkedLatest) }}
            </div>
            <div class="kpi-subtitle kpi-muted">
              Market-linked holdings (Stocks + MF) tracked from today
            </div>
          </template>

          <!-- Normal case: Has previous data -->
          <template v-else-if="prevSummary">
            <div class="kpi-title">Market Movement Today</div>
            <div
              class="kpi-value"
              :style="{ color: todayChangePct >= 0 ? 'var(--an-success)' : 'var(--an-error)' }"
            >
              {{ todayChangePct >= 0 ? '+' : '' }}{{ todayChangePct.toFixed(2) }}%
            </div>
            <div class="kpi-subtitle">
              {{ convertToInr(Math.abs(todayChange)) }}
              {{ todayChange >= 0 ? 'gain' : 'loss' }} on market-linked holdings
            </div>
            <!-- Show warning if change > 8% (likely new holdings added) -->
            <div v-if="hasSignificantChange" class="kpi-warning-badge">
              <i class="pi pi-exclamation-triangle"></i>
              <span>Large change detected in Portfolio - may include new holdings imported</span>
            </div>
            <div v-else class="kpi-subtitle kpi-muted">
              Stocks + Mutual Funds only
            </div>
          </template>

          <!-- Fallback: No data -->
          <template v-else>
            <div class="kpi-title">Market Movement Today</div>
            <div class="kpi-value" :style="{ color: '#9ca3af' }">
              -
            </div>
            <div class="kpi-subtitle kpi-muted">
              Awaiting data
            </div>
          </template>
        </div>
      </div>

      <!-- Chart Section -->
      <div class="chart-section flex-1">
        <div class="chart-title">Market-linked Value Trend</div>
        <div v-if="!hasMarketLinkedHoldings" class="empty-chart-state no-market-linked">
          <i class="pi pi-chart-line" style="font-size: 2rem; margin-bottom: 8px; color: #d1d5db;"></i>
          <div class="empty-title">No Market-linked Holdings</div>
          <div class="empty-description">
            Add stocks or mutual funds to track daily market changes
          </div>
        </div>
        <div v-else-if="!hasChartData" class="empty-chart-state">
          Not enough data points. Wait for daily updates to see the trend.
        </div>
        <Chart
          v-else
          type="line"
          :data="marketLinkedChartData"
          :options="lineChartOptions(true)"
          :plugins="[gridHighlightPlugin]"
          class="line-chart w-100"
        />
      </div>
    </div>

    <!-- Right section: Portfolio Diversity (unchanged) -->
    <div
      class="input-shadow donut p-20 br-10 d-flex flex-direction-column gap-20"
    >
      <span class="font-20 font-weight-700">Portfolio Diversity</span>
      <Chart
        type="doughnut"
        :data="donutChartData"
        :options="donutChartOptions"
        class="chart w-100"
      />
      <div class="custom-legend d-grid gap-10">
        <div
          v-for="(label, index) in donutChartData.labels"
          :key="index"
          class="legend-item d-flex flex-direction-column"
        >
          <div class="d-flex align-items-center">
            <span
              class="legend-color"
              :style="{
                backgroundColor:
                  donutChartData.datasets[0].backgroundColor?.[index] ||
                  chartColors[index % chartColors.length],
              }"
            ></span>
            <span class="font-10">
              {{ label }}:
              <b>{{ donutChartData.datasets[0].data[index] }}%</b>
            </span>
          </div>
          <span class="font-10 legend-amount">
            {{ convertToInr(donutChartData.rawValues?.[index] || 0) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart-wrapper {
  min-height: 400px;
  flex-wrap: wrap;
}

.chart {
  height: 200px;
}

.custom-legend {
  grid-template-columns: repeat(2, 1fr);
}

.legend-color {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 3px;
}

.donut {
  max-width: 350px;
}

.line,
.donut {
  flex: 1 1 0;
  min-width: 300px;
}

.legend-amount {
  margin-left: 14px;
  color: var(--an-gray);
}

/* KPI Cards */
.kpi-cards-row {
  display: flex;
  gap: 15px;
}

.kpi-card {
  flex: 1;
  padding: 16px;
  border-radius: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.kpi-title {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 4px;
}

.kpi-percent {
  font-size: 1rem;
  font-weight: 600;
  margin-left: 4px;
}

.kpi-subtitle {
  font-size: 0.6875rem;
  color: #9ca3af;
  margin-bottom: 8px;
}

.kpi-breakdown {
  font-size: 0.625rem;
  color: #9ca3af;
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.breakdown-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.breakdown-bullet {
  font-size: 0.75rem;
  line-height: 1;
}

.kpi-muted {
  font-style: italic;
}

/* Warning Badge for Significant Changes */
.kpi-warning-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: 6px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #92400e;
  line-height: 1.3;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.15);
}

.kpi-warning-badge i {
  font-size: 0.75rem;
  color: #f59e0b;
  flex-shrink: 0;
}

/* Chart Section */
.chart-section {
  display: flex;
  flex-direction: column;
}

.chart-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 12px;
}

.line-chart {
  height: 180px !important;
  flex: 1;
}

.empty-chart-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 180px;
  color: #9ca3af;
  font-size: 0.875rem;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

.empty-chart-state.no-market-linked {
  flex-direction: column;
  font-style: normal;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
}

.empty-description {
  font-size: 0.875rem;
  color: #9ca3af;
  line-height: 1.5;
}

/* Responsive */
@media (max-width: 768px) {
  .kpi-cards-row {
    flex-direction: column;
  }

  .kpi-card {
    width: 100%;
  }
}
</style>
